import { Text } from '@belong/ui';
import { isNil } from 'es-toolkit/compat';
import GoogleMapReact, { Props as GoogleMapReactProps } from 'google-map-react';
import type Supercluster from 'supercluster';
import { Card } from './card/card';
import { ComparableMarker } from './markers/comparable-marker';
import { CurrentHomeMarker } from './markers/current-home-marker';
import { RentedMarker } from './markers/rented-marker';

/**
 * The `properties` bag attached to each home before it is fed to supercluster.
 * `home` stays `any` because these are raw comparables payloads with no
 * generated model behind them — same as the caller's own `homes?: any[]`.
 */
export type ComparableHomeProperties = {
  cluster: false;
  category: string;
  homeId: number;
  home: any;
};

/**
 * What `useSupercluster` hands back for these points: either one of our own
 * home features or an aggregated cluster of them. Discriminated on
 * `properties.cluster`, via `isClusterFeature` below.
 */
type ComparableFeature =
  | Supercluster.PointFeature<ComparableHomeProperties>
  | Supercluster.ClusterFeature<Supercluster.AnyProps>;

function isClusterFeature(
  feature: ComparableFeature,
): feature is Supercluster.ClusterFeature<Supercluster.AnyProps> {
  return feature.properties.cluster === true;
}

type ComparablesMapProps = {
  // `number | null` rather than optional, so the caller's
  // `useState<number | null>` setter can be passed straight through.
  setActive: (index: number | null) => void;
  // `any[]`, matching the caller, because `homes[n].address.coordinates` is an
  // ungenerated API shape. `Record<string, unknown>` made every access an error.
  homes?: any[];
  // Null rather than absent: the caller holds these in `useState(null)`.
  hover?: number | null;
  active?: number | null;
  handleApiLoaded: (map: google.maps.Map) => void;
  gesture?: string;
  // Narrowed from `string` so it can index `ControlPosition`; these are the two
  // positions the callers actually pass.
  zoomControlPosition?: 'RIGHT_TOP' | 'RIGHT_BOTTOM';
  hasPointer?: boolean;
  clusters?: ComparableFeature[];
  points?: Supercluster.PointFeature<ComparableHomeProperties>[];
  onClusterClick: (args: {
    cluster: ComparableFeature;
    latitude: number;
    longitude: number;
  }) => () => void;
} & GoogleMapReactProps;

export function ComparablesMap({
  gesture = 'greedy',
  zoomControlPosition = 'RIGHT_TOP',
  setActive,
  homes = [],
  hover,
  active,
  handleApiLoaded,
  clusters = [],
  points = [],
  onClusterClick,
  ...rest
}: ComparablesMapProps) {
  return (
    <>
      <GoogleMapReact
        {...rest}
        onGoogleApiLoaded={({ map }) => {
          handleApiLoaded(map);
        }}
        bootstrapURLKeys={{
          key: `https://maps.googleapis.com/maps/api/js?key=${process.env.NEXT_PUBLIC_GOOGLE_MAPS_API_KEY}&libraries=placesgeometry,drawing&language=en`,
        }}
        options={(e) => {
          return {
            gestureHandling: gesture,
            clickableIcons: false,
            fullscreenControl: false,
            zoomControlOptions: {
              position: e.ControlPosition[zoomControlPosition],
            },
            scrollwheel: false,
          };
        }}
        center={{
          lat: parseFloat(homes[0].address.coordinates?.latitude || 0),
          lng: parseFloat(homes[0].address.coordinates?.longitude || 0),
        }}
        zoom={8}
      >
        {clusters.map((cluster) => {
          // GeoJSON's `Position` is typed `number[]`, so indexing it yields
          // `number | undefined`. A Point always carries both coordinates, and
          // these features are built with exactly two in
          // `comparables-step-map.tsx`, so the assertions have no runtime effect.
          const longitude = cluster.geometry.coordinates[0]!;
          const latitude = cluster.geometry.coordinates[1]!;

          if (isClusterFeature(cluster)) {
            const pointCount = cluster.properties.point_count;

            return (
              <Marker
                key={`cluster-${cluster.id}`}
                lat={latitude}
                lng={longitude}
              >
                <div
                  role="button"
                  tabIndex={0}
                  className="flex items-center justify-center rounded-full bg-navy p-xs text-white"
                  style={{
                    width: `${10 + (pointCount / points.length) * 20}px`,
                    height: `${10 + (pointCount / points.length) * 20}px`,
                  }}
                  onClick={onClusterClick({ cluster, latitude, longitude })}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      onClusterClick({ cluster, latitude, longitude })();
                    }
                  }}
                >
                  <Text fontWeight="semibold">{pointCount}</Text>
                </div>
              </Marker>
            );
          } else {
            if (cluster.properties.home.current) {
              return (
                <CurrentHomeMarker
                  key={cluster.properties.homeId}
                  lat={latitude}
                  lng={longitude}
                  home={cluster.properties.home}
                  isActive={active === cluster.properties.homeId}
                  isHover={hover === cluster.properties.homeId}
                />
              );
            }

            if (cluster.properties.home.recentlyOffMarket) {
              return (
                <RentedMarker
                  key={cluster.properties.homeId}
                  lat={latitude}
                  lng={longitude}
                  home={cluster.properties.home}
                  isActive={active === cluster.properties.homeId}
                  isHover={hover === cluster.properties.homeId}
                />
              );
            }

            return (
              <ComparableMarker
                key={cluster.properties.homeId}
                lat={latitude}
                lng={longitude}
                home={cluster.properties.home}
                isActive={active === cluster.properties.homeId}
                isHover={hover === cluster.properties.homeId}
              />
            );
          }
        })}
      </GoogleMapReact>

      {!isNil(active) && (
        <Card home={homes[active]} onHide={() => setActive(null)} />
      )}
    </>
  );
}

/**
 * Positions its children on the map — `google-map-react` reads `lat`/`lng` off
 * this element. Returning `children` directly types the component as
 * `ReactNode`, which is not a valid JSX component return, hence the fragment.
 */
const Marker = ({
  children,
}: {
  children: React.ReactNode;
  lat: number;
  lng: number;
}) => <>{children}</>;
